import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentLanguageCode } from '@/shared/constants';
import { getContentLanguageLabel, getMultilingualValue } from '@/shared/utils/languageUtils';
import { Work } from '@/shared/types';
import { PreviewModal } from '@/shared/components/PreviewModal';
import { ModalCloseButton } from '@/shared/components/ModalCloseButton';
import { useModalA11y } from '@/shared/hooks/useModalA11y';
import { WorkQrModal } from './WorkQrModal';
import { WorkConfirmDialogs } from './WorkConfirmDialogs';
import { useCreateWorkForm, WORK_SECTION_PANEL_ID, workSectionTabId } from '../hooks/useCreateWorkForm';
import { CreateWorkSidebar } from './CreateWorkSidebar';
import { WorkMetadataFields } from './WorkMetadataFields';
import { WorkCoverPanel } from './WorkCoverPanel';
import { WorkAudioPanel } from './WorkAudioPanel';
import { AudioDescriptionSection } from './AudioDescriptionSection';
import { buildWorkQrUrl } from '@/features/qr/utils/qrUrl';

export interface WorkFormData {
  coverImage?: File | null;
  audioFile?: Record<ContentLanguageCode, File | null>;
  status: Record<ContentLanguageCode, 'draft' | 'published'>;
  title?: Record<ContentLanguageCode, string>;
  floor?: string;
  room?: string;
  pillNumber: number | string;
  order: number;
  transcriptionTab: 'text' | 'title' | 'captions';
  transcription: Record<ContentLanguageCode, string>;
  activeLanguages: ContentLanguageCode[];
  currentLanguage: ContentLanguageCode;
}

interface CreateWorkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  museumId: string;
  exhibitionId: string;
  exhibitionActiveLanguages: ContentLanguageCode[];
  exhibitionCurrentLanguage?: ContentLanguageCode;
  onExhibitionLanguageChange?: (lang: ContentLanguageCode) => void;
  exhibitionColor?: string;
  work?: Work | null;
}

export const CreateWorkModal: React.FC<CreateWorkModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  museumId,
  exhibitionId,
  exhibitionActiveLanguages,
  exhibitionCurrentLanguage,
  onExhibitionLanguageChange,
  exhibitionColor,
  work,
}) => {
  const form = useCreateWorkForm({
    isOpen,
    onClose,
    onSuccess,
    museumId,
    exhibitionId,
    exhibitionActiveLanguages,
    exhibitionCurrentLanguage,
    work,
  });
  const { t } = useTranslation();
  const [workPreviewOpen, setWorkPreviewOpen] = useState(false);
  const canPreview = Boolean(form.museum?.pwaUrlBase);
  const modalRef = useModalA11y({
    isOpen,
    onClose: form.handleRequestClose,
    initialFocusSelector: '.create-work-modal-close, .modal-close-button',
  });
  const titleId = 'create-work-modal-title';

  useEffect(() => {
    if (!isOpen) {
      setWorkPreviewOpen(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <WorkConfirmDialogs
        showPublishConfirmation={form.showPublishConfirmation}
        showPublishSuccess={form.showPublishSuccess}
        showDeleteConfirmation={form.showDeleteConfirmation}
        showUnsavedConfirm={form.showUnsavedConfirm}
        deleting={form.deleting}
        onCancelPublish={form.handleCancelPublish}
        onConfirmPublish={form.handleConfirmPublish}
        onCloseSuccess={form.handleCloseSuccess}
        onCancelDelete={form.handleCancelDelete}
        onConfirmDelete={form.handleConfirmDelete}
        onContinueEditing={() => form.setShowUnsavedConfirm(false)}
        onDiscardChanges={() => {
          form.setShowUnsavedConfirm(false);
          form.handleClose();
        }}
      />

      {work && form.museum?.pwaUrlBase && (
        <PreviewModal
          isOpen={workPreviewOpen}
          onClose={() => setWorkPreviewOpen(false)}
          previewUrl={buildWorkQrUrl(form.museum.pwaUrlBase, exhibitionId, work.id)}
          selectedLanguage={form.currentLanguage}
          onLanguageChange={(lang) => {
            const code = lang as ContentLanguageCode;
            form.setCurrentLanguage(code);
            onExhibitionLanguageChange?.(code);
          }}
          availableLanguages={form.activeLanguages}
        />
      )}

      {work && form.canUseQrCode && form.museum?.pwaUrlBase && (
        <WorkQrModal
          isOpen={form.showWorkQrModal}
          onClose={() => form.setShowWorkQrModal(false)}
          pwaUrlBase={form.museum.pwaUrlBase}
          exhibitionId={exhibitionId}
          workId={work.id}
          workName={
            form.title[form.currentLanguage] ||
            getMultilingualValue(form.title) ||
            getMultilingualValue(work.name) ||
            ''
          }
        />
      )}

      <div className="create-work-modal-overlay" onClick={form.handleRequestClose} role="presentation">
        <div
          ref={modalRef}
          className="create-work-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <h2 id={titleId} className="sr-only">
            {form.isEditMode ? t('works.editWork') : t('works.addWork')}
          </h2>
          <div className="create-work-modal-header">
            <ModalCloseButton onClick={form.handleRequestClose} className="create-work-modal-close" />
          </div>

          <div className="create-work-modal-body">
            <CreateWorkSidebar
              work={work}
              museum={form.museum}
              isQrCodeEnabled={form.isQrCodeEnabled}
              canUseQrCode={form.canUseQrCode}
              currentLanguage={form.currentLanguage}
              statusByLanguage={form.statusByLanguage}
              saving={form.saving}
              activeSection={form.activeSection}
              onSectionChange={form.setActiveSection}
              onOpenQrModal={() => form.setShowWorkQrModal(true)}
              onPreview={() => setWorkPreviewOpen(true)}
              canPreview={canPreview}
              onSave={form.handleSave}
              onRequestClose={form.handleRequestClose}
            />

            <div
              className="create-work-content"
              role="tabpanel"
              id={WORK_SECTION_PANEL_ID}
              aria-labelledby={workSectionTabId(form.activeSection)}
            >
              <div className="create-work-panel-header">
                <h3 className="create-work-panel-title">{t(`works.sections.${form.activeSection}.title`)}</h3>
                <p className="create-work-panel-intro">{t(`works.sections.${form.activeSection}.intro`)}</p>
              </div>

              <div className="create-work-language-tabs" role="tablist" aria-label={t('exhibitions.chooseLanguage')}>
                {form.activeLanguages.map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    role="tab"
                    aria-selected={form.currentLanguage === lang}
                    className={`language-tab ${form.currentLanguage === lang ? 'active' : ''}`}
                    onClick={() => {
                      form.setCurrentLanguage(lang);
                      onExhibitionLanguageChange?.(lang);
                    }}
                  >
                    {getContentLanguageLabel(lang)}
                  </button>
                ))}
              </div>

              {form.activeSection === 'metadata' && (
                <WorkMetadataFields
                  isEditMode={form.isEditMode}
                  museum={form.museum}
                  currentLanguage={form.currentLanguage}
                  activeLanguages={form.activeLanguages}
                  title={form.title}
                  setTitle={form.setTitle}
                  statusByLanguage={form.statusByLanguage}
                  setStatusByLanguage={form.setStatusByLanguage}
                  floor={form.floor}
                  setFloor={form.setFloor}
                  room={form.room}
                  setRoom={form.setRoom}
                  pillNumber={form.pillNumber}
                  setPillNumber={form.setPillNumber}
                  order={form.order}
                  setOrder={form.setOrder}
                  allWorks={form.allWorks}
                  statusDropdownOpen={form.statusDropdownOpen}
                  setStatusDropdownOpen={form.setStatusDropdownOpen}
                  orderDropdownOpen={form.orderDropdownOpen}
                  setOrderDropdownOpen={form.setOrderDropdownOpen}
                  floorDropdownOpen={form.floorDropdownOpen}
                  setFloorDropdownOpen={form.setFloorDropdownOpen}
                  roomDropdownOpen={form.roomDropdownOpen}
                  setRoomDropdownOpen={form.setRoomDropdownOpen}
                  getAvailableRooms={form.getAvailableRooms}
                  markDirty={form.markDirty}
                  closeAllDropdownsExcept={form.closeAllDropdownsExcept}
                  onDeleteClick={form.handleDeleteClick}
                />
              )}
              {form.activeSection === 'cover' && (
                <WorkCoverPanel
                  currentLanguage={form.currentLanguage}
                  coverPreview={form.coverPreview}
                  coverInputRef={form.coverInputRef}
                  imageAlt={form.imageAlt}
                  setImageAlt={form.setImageAlt}
                  markDirty={form.markDirty}
                  onCoverClick={form.handleCoverClick}
                  onCoverSelect={form.handleCoverSelect}
                  onCoverModify={form.handleCoverModify}
                  onCoverDelete={form.handleCoverDelete}
                />
              )}
              {form.activeSection === 'audioDescription' && (
                <AudioDescriptionSection form={form.audioDescription} exhibitionColor={exhibitionColor} />
              )}
              {form.activeSection === 'audio' && (
                <WorkAudioPanel
                  currentLanguage={form.currentLanguage}
                  transcription={form.transcription}
                  setTranscription={form.setTranscription}
                  audioPreview={form.audioPreview}
                  audioInputRef={form.audioInputRef}
                  audioUploadError={form.audioUploadError}
                  exhibitionColor={exhibitionColor}
                  markDirty={form.markDirty}
                  onAudioClick={form.handleAudioClick}
                  onAudioSelect={form.handleAudioSelect}
                  onRemoveAudio={form.handleRemoveAudio}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
